<template>
  <div class="base-dropdown-wrapper">
    <span v-if="label" class="label">{{ label }}</span>
    <base-dropdown-wrap class="base-dropdown" :class="classes">
      <template #trigger>
        <slot name="trigger"></slot>
        <span class="base-dropdown__icon">
          <slot name="icon">
            <svg-icon
              class="base-dropdown__base-icon"
              name="chevron"
            ></svg-icon>
          </slot>
        </span>
      </template>
      <template #default>
        <slot></slot>
        <ul class="base-dropdown__menu">
          <slot
            ref="dropdownContent"
            name="content"
            :on-item-click="onItemClick"
          ></slot>
        </ul>
      </template>
    </base-dropdown-wrap>
  </div>
</template>

<script>
import SvgIcon from '../SvgIcon/SvgIcon.vue'
import BaseDropdownWrap from './Wrap.vue'

export default {
  name: 'BaseDropdown',
  props: {
    direction: {
      type: String,
      default: 'bottom',
    },
    size: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'lg'].includes(value),
    },
    label: {
      type: String,
      default: '',
    },
    responsiveWidth: { Boolean },
    dropdownMenuPosition: {
      type: String,
      default: 'left',
    },
    hasIcon: {
      type: Boolean,
      default: false,
    },
    search: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['onSelect'],
  components: {
    BaseDropdownWrap,
    SvgIcon,
  },
  computed: {
    classes() {
      return {
        'base-dropdown--top': this.direction === 'top',
        'base-dropdown--lg': this.size === 'lg',
        'base-dropdown--responsive-width': this.responsiveWidth,
        'base-dropdown--left': this.dropdownMenuPosition === 'left',
        'base-dropdown--right': this.dropdownMenuPosition === 'right',
        'base-dropdown--has-icon': this.hasIcon,
        'base-dropdown--search': this.search,
      }
    },
  },
  methods: {
    onItemClick(data) {
      this.$refs.dropdownContent.close()
      this.$emit('onSelect', data)
    },
  },
}
</script>

<style lang="scss">
.base-dropdown {
  --base-dropdown--text: var(--text-3);
  --base-dropdown--hoverText: var(--text-1);
  --base-dropdown--background: var(--bg-1);
  --base-dropdown--triggerBackground: var(--bg-1);
  --base-dropdown--triggerBorderColor: var(--bg-3);
  --base-dropdown--triggerColor: var(--text-1);
  --base-dropdown--borderColor: var(--text-1);
  --base-dropdown--iconColor: currentColor;

  --base-dropdown--paddingH: 16px;
  --base-dropdown--paddingV: 17px;

  --base-dropdown--bodyPaddingH: var(--base-dropdown--paddingH);
  --base-dropdown--bodyPaddingV: calc(var(--base-dropdown--paddingV) / 2);

  color: var(--base-dropdown--text);

  &[disabled] {
    pointer-events: none;
    opacity: 0.5;
  }

  &:hover,
  &.is-open {
    .base-dropdown-wrap__trigger {
      color: var(--base-dropdown--hoverText);
    }
  }

  &.is-open {
    .base-dropdown-wrap__trigger {
      border-color: currentColor;
    }
  }

  // Styles to base

  .base-dropdown-wrap {
    &__trigger {
      padding: var(--base-dropdown--paddingV) var(--base-dropdown--paddingH);
      padding-right: calc(var(--base-dropdown--paddingH) * 4);
      display: flex;
      align-items: center;
      position: relative;
      font-size: inherit;
      cursor: pointer;
      background: var(--base-dropdown--triggerBackground);
      color: var(--base-dropdown--triggerColor);
      min-height: 40px;
      border-radius: 8px;
      transition: color 200ms ease;
      width: 100%;
      border: 1px solid var(--base-dropdown--triggerBorderColor);
      font-weight: 500;
      transition: 200ms ease;
      white-space: nowrap;

      svg {
        fill: white;
      }

      .icon {
        margin-right: 6px;
      }
    }

    &__content {
      padding-top: 10px;
      position: absolute;
      top: 100%;
    }
  }

  &--top {
    .base-dropdown-wrap {
      &__content {
        top: auto;
        padding-top: 0;
        bottom: 100%;
        padding-bottom: 10px;
      }
    }
  }

  &--right {
    .base-dropdown-wrap {
      &__content {
        right: 0;
      }
    }
  }

  &--left {
    .base-dropdown-wrap {
      &__content {
        left: 0;
      }
    }
  }

  &--lg {
    .base-dropdown-wrap {
      &__trigger {
        min-height: 54px;
      }
    }
  }

  &--responsive-width {
    .base-dropdown-wrap__trigger {
      width: 100%;
    }
  }

  // Custom styles
  .base-dropdown__icon {
    display: inline-flex;
    align-items: center;
    position: absolute;
    right: calc(var(--base-dropdown--paddingH) * 1.5);
  }

  &__base-icon {
    display: inline-flex;
  }

  ul {
    max-height: 300px;
    overflow-y: scroll;
    overflow-x: hidden;
    position: relative;
  }
  ul.base-dropdown__menu {
    scrollbar-width: thin;
    background: var(--bg-4);
    box-shadow: 0 2px 12px 0 rgba($color: #000, $alpha: 0.1);
    border-radius: 10px;
    list-style: none;
    padding: 0;
    margin: 0;
    min-width: 100%;
    width: max-content;
    max-width: 500px;
    padding: var(--base-dropdown--bodyPaddingV) 0;

    li {
      padding: var(--base-dropdown--bodyPaddingV)
        var(--base-dropdown--bodyPaddingH);
      display: flex;
      align-items: center;
      color: inherit;
      cursor: pointer;
      transition: 200ms ease;
      opacity: 0.8;
      font-weight: 500;

      .icon {
        margin-right: 6px;
      }

      &:hover {
        opacity: 1;
        color: var(--base-dropdown--hoverText);
        background: var(--bg-35);
      }
    }

    .menu-input {
      background: red;
    }
  }

  &--search {
    ul.base-dropdown__menu {
      padding-top: 0px;
    }
  }

  .light & {
    --base-dropdown--triggerBackground: var(--bg-2);
  }
}

.base-dropdown-wrapper {
  display: flex;
  flex-direction: column;
}

.label {
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  color: var(--text-3);
  margin-bottom: 8px;
  text-align: start;
}

.dropdown-input {
  position: sticky;
  top: 0;
  left: 0;
  width: max-content;
  padding: 10px 10px 8px 16px;
  font-size: 1rem;
  background-color: var(--bg-2);
  outline: none;
  box-shadow: none;
  border: none;
  color: var(--text-1);
  z-index: 100;
}
</style>
